import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SalaryUnits } from '../../../../../firebase/firestore/documents/requisition';
import { getConsistentDateFormat } from '../../../../../utils/dateUtils';
import { RequisitionDetailViewModel } from '../utils/RequisitionDetailsViewModel';
import { NewRequisition, ShiftDetail, NewShift } from '../types/requistionDetails';
import {
  buildShiftScheduleViewModel,
  buildFlexibilityViewModel,
  buildComparisonViewModel,
  buildOverlapShiftSchedule,
  IntersectionDayDetail,
} from '../utils/requisitionDetailsUtils';

const STATUS_COLOR: Record<string, string> = {
  OPEN: 'blue',
  PROCESSING: 'teal',
  FILLED: 'green',
  CANCELLED: 'red',
};

const buildWageLabel = (
  wage: NewRequisition['wage'] | undefined,
  locale: string,
  t: (key: string) => string,
): string | undefined => {
  if (!wage?.visible || wage.value == null) return undefined;

  const fmt = (v: number) =>
    `$${v.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const range = wage.maxValue != null ? ` - ${fmt(wage.maxValue)}` : '';
  const unit = wage.unit === SalaryUnits.CUSTOM ? wage.customUnit : t(wage.unit.toLowerCase());

  return `${fmt(wage.value)}${range}/${unit}`;
};

const fmtHours = (v: number | undefined, locale: string): string | undefined =>
  v != null ? v.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : undefined;

/**
 * ALL business logic for the v2 requisition detail view.
 *
 *  granular useMemo blocks for:
 *   - Header data (wage, status, dates) only recomputes when requisition/locale changes
 *   - Comparison only recomputes when candidate availability changes
 *   - Shift schedule only recomputes when shifts or candidate data changes
 *   - Final assembly is a cheap object merge
 *
 * This prevents unnecessary re-renders of downstream components
 * (e.g. RequisitionHeader won't re-render when candidate data changes).
 */
export const useRequisitionDetails = (
  requisition: NewRequisition | undefined,
  shouldCompare = false,
  candidateAvailabilities?: Record<string, ShiftDetail>,
  candidateNightAvailabilities?: Record<string, ShiftDetail>,
  isShiftIntersectable = false,
  intersectionShift?: Record<string, IntersectionDayDetail>,
): RequisitionDetailViewModel | null => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  // 1. Header data: only recomputes when requisition identity or locale changes
  const headerData = useMemo(() => {
    if (!requisition) return null;

    const minLabel = fmtHours(requisition.minHours, locale);
    const maxLabel = fmtHours(requisition.maxHours, locale);
    const guaranteedLabel = fmtHours(requisition.guaranteedHours, locale);

    return {
      requisitionId: requisition.requisitionId,
      statusLabel: requisition.status,
      statusColorScheme: requisition.status ? STATUS_COLOR[requisition.status] ?? 'blue' : undefined,
      isFullTime: !!requisition.fullTime,
      isSeasonal: !!requisition.seasonal,
      partTimeHoursLabel:
        !requisition.fullTime && requisition.partTimeHours != null ? requisition.partTimeHours.toFixed(2) : undefined,
      wageLabel: buildWageLabel(requisition.wage, locale, t),
      effectiveDateLabel: requisition.effectiveDate ? getConsistentDateFormat(requisition.effectiveDate, 'full') : undefined,
      seasonalEndDateLabel:
        requisition.seasonal && requisition.endDate ? getConsistentDateFormat(requisition.endDate, 'full') : undefined,
      contractedHours: {
        minLabel,
        maxLabel,
        guaranteedLabel,
        hasAny: minLabel != null || maxLabel != null || guaranteedLabel != null,
      },
      note: requisition.note,
    };
  }, [requisition, locale, t]);

  // 2. Comparison: recomputes when candidate availability changes
  const comparison = useMemo(() => {
    if (!requisition || !shouldCompare || !requisition.shifts) return undefined;
    return buildComparisonViewModel(requisition.shifts, candidateAvailabilities, candidateNightAvailabilities, t);
  }, [requisition, shouldCompare, candidateAvailabilities, candidateNightAvailabilities, t]);

  // 3. Shift schedule: recomputes when shifts or intersection/candidate data changes
  const shiftSchedule = useMemo(() => {
    if (!requisition) return undefined;

    let scheduleShifts: NewShift | undefined = requisition.shifts;
    let scheduleCandAvail = candidateAvailabilities;
    let scheduleCandNightAvail = candidateNightAvailabilities;

    if (isShiftIntersectable) {
      if (intersectionShift) {
        scheduleShifts = buildOverlapShiftSchedule(requisition, intersectionShift);
      }
      scheduleCandAvail = undefined;
      scheduleCandNightAvail = undefined;
    }

    return scheduleShifts ? buildShiftScheduleViewModel(scheduleShifts, t, scheduleCandAvail, scheduleCandNightAvail) : undefined;
  }, [requisition, candidateAvailabilities, candidateNightAvailabilities, isShiftIntersectable, intersectionShift, t]);

  //   4. Flexibility: derived from shifts + comparison
  const flexibility = useMemo(() => {
    if (!requisition?.shifts) return undefined;
    return buildFlexibilityViewModel(requisition.shifts, comparison);
  }, [requisition, comparison]);

  return useMemo(() => {
    if (!headerData) return null;

    return {
      ...headerData,
      shiftSchedule,
      flexibility,
      comparison,
    };
  }, [headerData, shiftSchedule, flexibility, comparison]);
};
